import React, {useCallback, useEffect, useRef, useState} from 'react'
import styles from './SsoConfig.module.scss'
import {
  Accordion,
  AccordionTypes,
  Alert,
  Button,
  ButtonType,
  FormFieldSize,
  Icon,
  Switch,
  Tag,
  TagType,
  TextArea,
  Tooltip,
  AlertTypes
} from '@digicert/dcone-common-ui'
import {faArrowToBottom, faTrashAlt} from '@digicert/dcone-common-ui/src/components/icons/light'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faArrowFromBottom, faCheck, faCopy} from '@fortawesome/pro-light-svg-icons'
import {ReactComponent as StepOneIcon} from '../../../assets/images/step-1.svg'
import {ReactComponent as StepTwoIcon} from '../../../assets/images/step-2.svg'
import {ReactComponent as StepThreeIcon} from '../../../assets/images/step-3.svg'
import {IconProp} from '@fortawesome/fontawesome-svg-core'
import {translate} from '../../helpers/utils'
import {ExtendedSsoConfigProps, SsoConfigStateProps} from './SsoConfig.types'
import {connect} from 'react-redux'
import {IAppState} from '../../../core/store/store'
import xml2js from 'xml2js'
import {ICertificate} from '../../../reducers/Reducers.interfaces'
import {parseCertificate} from '../../../secure-pages/tenants/create-account-sign-in/CertificateParser'
import moment from 'moment'
import {createAccountSignIn, createIdpMetadata, getAccountSignIn} from './SsoConfig.actions'
import {DISABLED, ENABLED} from '../../../core/constants'
import {Link, useParams} from 'react-router-dom'
import fileDownload from 'js-file-download'
import Divider from '../../components/divider/Divider'
import {Translate} from 'react-localize-redux'
import cn from 'classnames'

const SsoConfig = (
  {
    account,
    onCancel,
    ssoSaml,
    federation,
    createIdpMetadata,
    createAccountSignIn,
    getAccountSignIn,
    onChange,
    primaryAccountId
  }: ExtendedSsoConfigProps) => {
  const { id } = useParams()
  const [tenant, setTenant] = useState(account)
  const [isSsoEnabled, setSsoEnabled] = useState(ssoSaml)
  const [showError, setShowError] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [fileName, setFileName] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [errorTitle, setErrorTitle] = useState('')
  const [idpMetaData, setIdpMetaData] = useState<any>()
  const [idpUrl, setIdpUrl] = useState('')
  const [idpCertificates, setIdpCertificates] = useState<ICertificate[]>([])
  const [accountSigninResponse, setAccountSigninResponse] = useState<any>(undefined)
  const [thumbprints, setThumbprints] = useState<string[]>([])
  const fileReaderRef = useRef<any>(null);
  const certificateReaderRef = useRef<any>(null);
  const [copied, setCopied] = useState<boolean>(false)

  const setErrorMessaging = (title: string, message: string) => {
    setErrorTitle('')
    setErrorMessage('')
    setErrorTitle(title)
    setErrorMessage(message)
  }

  const cancelSso = () => {
    setSsoEnabled(ssoSaml)
    if(onCancel) {
      onCancel()
    }
  }

  const reader: FileReader = new FileReader()

  const uploadedMetaData = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files

      if (files) {
        reader.readAsText(files[0])
        setFileName(files[0].name)
      }

      let metadata: string | ArrayBuffer | null = ''
      let extractedData = ''
      let idpUrl = ''
      let mainNode = ''
      let certType = ''
      let certificateNode = ''

      reader.onload = (event) => {
        if(event.target) {
          metadata = event.target.result
          const processors = xml2js.processors
          const parser = new xml2js.Parser({tagNameProcessors: [processors.stripPrefix]})

          parser.parseString(metadata as string, function (error: any, result: any) {
            if(error) {
              setFileName('')
              setShowError(true)
              setErrorMessaging('Error', translate('ssoSaml.invalidXml') as string)
              return
            }

            mainNode = result['EntityDescriptor']

            // explore solution to simplify logic and data extraction
            if (!mainNode && result['EntitiesDescriptor']) {
              mainNode = result['EntitiesDescriptor']['EntityDescriptor'][0]
            }

            if (mainNode) {
              if (mainNode['IDPSSODescriptor'][0]['KeyDescriptor']) {
                certType = mainNode['IDPSSODescriptor'][0]['KeyDescriptor'][0].$.use
                if (certType === 'signing') {
                  certificateNode = mainNode['IDPSSODescriptor'][0]['KeyDescriptor'][0]
                } else if (mainNode['IDPSSODescriptor'][0]['KeyDescriptor'][1] && mainNode['IDPSSODescriptor'][0]['KeyDescriptor'][1].$.use === 'signing') {
                  certificateNode = mainNode['IDPSSODescriptor'][0]['KeyDescriptor'][1]
                }
                if (certificateNode) {
                  extractedData = certificateNode['KeyInfo'][0]['X509Data'][0]['X509Certificate'][0].replace('-----BEGIN CERTIFICATE-----', '').replace('-----END CERTIFICATE-----', '').replace(/\n|\r/g, '').replace(/\s+/g, '')
                }
              }
              idpUrl = mainNode['IDPSSODescriptor'][0]['SingleSignOnService'][0].$.Location
            }
          })

          if (!idpUrl) {
            setShowError(true)
          }

          else {
            setIdpMetaData(metadata)
            setIdpUrl(idpUrl)
            setShowNotification(true)
            setShowError(false)
          }

          // SHA-1 on certificate binary data
          if(extractedData) {
            const forge = require('node-forge')
            const md = forge.md.sha256.create()
            md.start()
            md.update(window.atob(extractedData))
            const digest = md.digest()

            // print as HEX
            const hex = digest.toHex()
            const certificates = parseCertificate(extractedData)

            if(idpCertificates.find(item => item.common_name === certificates[0].subject.components.commonName)) {
              setShowNotification(false)
              setShowError(true)
              setErrorMessaging('Error', translate('ssoSaml.duplicateCertificate') as string)
            }

            else {
              if (certificates[0].isValid) {
                let tmpCerts: Array<ICertificate> = [
                  ...idpCertificates,
                  {
                    certificate: extractedData,
                    common_name: certificates[0].subject.components.commonName,
                    expiration_date: certificates[0].validTo,
                    thumb_print: hex
                  }
                ]
                setIdpCertificates(tmpCerts)

                let tmpThumbPrints: Array<any> = [...thumbprints, hex]
                setThumbprints(tmpThumbPrints)
              }

              else if (!certificates[0].isValid) {
                setShowNotification(false)
                setShowError(true)
                setErrorMessaging(translate('ssoSaml.expiredSamlCertificateTitle') as string, translate('ssoSaml.expiredSamlCertificateDesc') as string)
              }
            }
          }
        }
      }
    }, [idpCertificates]
  )

  const resetErrorMessaging = () => {
    setShowNotification(false)
    setShowError(false)
    setErrorMessaging('', '')
  }

  const uploadCertificate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      setFileName('')

      if(files) {
        reader.readAsText(files[0])
        setFileName(files[0].name)
      }

      let extractedData = ''

      reader.onload = (event: any) => {
        extractedData = event.target.result.replace(/\n|\r/g, '').replace('-----BEGIN CERTIFICATE-----', '').replace('-----END CERTIFICATE-----', '')
        const certificates = parseCertificate(extractedData)

        if(extractedData && certificates.length > 0  && certificates[0].isValid) {
          const forge = require('node-forge')
          const md = forge.md.sha256.create()
          md.start()
          md.update(window.atob(extractedData))
          const digest = md.digest()
          const hex = digest.toHex()

          if(idpCertificates.find(item => item.thumb_print === hex)) {
            setShowNotification(false)
            setShowError(true)
            setErrorMessaging('Error', translate('ssoSaml.duplicateCertificate') as string)
          }

          else {
            resetErrorMessaging()
            let tmpCerts: Array<ICertificate> = [
              ...idpCertificates,
              {
                certificate: extractedData,
                common_name: certificates[0].subject.components.commonName,
                expiration_date: certificates[0].validTo,
                thumb_print: hex
              }
            ]
            setIdpCertificates(tmpCerts)
            let tmpThumbPrints: Array<any> = [...thumbprints, hex]
            setThumbprints(tmpThumbPrints)
          }
        }

        else if (certificates.length > 0 && !certificates[0].isValid) {
          setShowNotification(false)
          setShowError(true)
          setErrorMessaging('Error', translate('ssoSaml.expiredCertificate') as string)
        }

        else {
          setShowNotification(false)
          setShowError(true)
          setErrorMessaging('Error', translate('singleSignOn.invalidCertificate') as string)
        }
      }
    }, [idpCertificates]
  )

  const handleSwitchChange = useCallback(
    (checked: boolean) => {
      setSsoEnabled(checked)
    }, [isSsoEnabled]
  )

  const deleteCertificate = useCallback(
    (certName?: string) => {
      let idpCerts: Array<ICertificate> = [...idpCertificates]
      idpCerts = idpCerts.filter((item) => item.common_name !== certName)
      setIdpCertificates(idpCerts)
    }, [idpCertificates]
  )

  const connectIdpView = () => {
    return (
      <div className={styles.connectDigiCert}>
        {showError && (
          <div className={styles.banner}>
            <Alert
              bannerType={AlertTypes.ERROR}
              description={errorMessage}
              showDismissIcon
              title={errorTitle}
            />
          </div>
        )}

        {typeof idpMetaData === 'undefined' && (
          <>
            <p>{translate('ssoSaml.downloadIdpMetaDataDescription')}</p>

            <div className={styles.browseFileContainer}>
              <Button buttonType={ButtonType.SECONDARY} icon={<FontAwesomeIcon icon={faArrowFromBottom as IconProp}/>}>
                <label className={styles.browseFileLabel}>
                  <span>{translate('common.form.buttons.uploadIdpMetaData')}</span>
                  <input
                    className={styles.browseFileInput}
                    type='file'
                    onChange={(e) => uploadedMetaData(e)}
                    accept={'.xml'}
                  />
                </label>
              </Button>
            </div>
          </>
        )}

        {typeof idpMetaData !== 'undefined' && (
          <>
            {showNotification && (
              <div className={styles.banner}>
                <Alert
                  bannerType="success"
                  description={fileName}
                  showDismissIcon
                  title={translate('ssoSaml.fileUploaded')}
                />
              </div>
            )}

            <div className={styles.card}>
              <header className={styles.header}>
                <span>{translate('ssoSaml.idpDetails')}</span>
                <Tag type={TagType.ACTIVE}>
                  <Translate id={'common.statuses.active'}/>
                </Tag>
              </header>
              <dl className={styles.list}>
                <div className={styles.item}>
                  <dt>{translate('ssoSaml.idpUrl')}:</dt>
                  <dd>{idpUrl}</dd>
                </div>
                <div className={styles.item}>
                  <dt>{translate('ssoSaml.ssoUrl')}:</dt>
                  <dd>{federation.service_provider_config ? federation.service_provider_config.acs_url:''}</dd>
                </div>
              </dl>
            </div>

            <div className={styles.metaDataActions}>
              <div className={styles.browseFileContainer}>
                <button>
                  <FontAwesomeIcon icon={faArrowFromBottom as IconProp}/>
                  <label className={styles.browseFileLabelNoPadding}>
                    <span>{translate('common.form.buttons.replaceIdpMetaData')}</span>
                    <input
                      ref={fileReaderRef}
                      className={styles.browseFileInput}
                      type='file'
                      onChange={(e) => uploadedMetaData(e)}
                      accept={'.xml'}
                    />
                  </label>
                </button>
              </div>
            </div>

            {idpCertificates.map((item, index) => {
              return (
                <div className={styles.card} key={item.common_name+'-'+index}>
                  <header className={styles.header}>
                    <div>
                      <span className={styles.title}>{translate('ssoSaml.samlCertDetails')}</span>
                      <Tag type={moment(item.expiration_date).isBefore() ? TagType.DANGER : TagType.ACTIVE}>
                        {moment(item.expiration_date).isBefore() ?
                          (<Translate id={'common.statuses.expired'}/>) :
                          (<Translate id={'common.statuses.valid'}/>)
                        }
                      </Tag>
                    </div>
                    <button className={styles.deleteCert}
                            onClick={() => deleteCertificate(item.common_name)}>
                      <Icon icon={faTrashAlt}></Icon>
                    </button>
                  </header>
                  <dl className={styles.list}>
                    <div className={styles.item}>
                      <dt>{translate('ssoSaml.commonName')}:</dt>
                      <dd>{item.common_name}</dd>
                    </div>
                    <div className={styles.item}>
                      <dt>{translate('ssoSaml.thumbprint')}:</dt>
                      <dd>{item.thumb_print}</dd>
                    </div>
                    <div className={styles.item}>
                      <dt>{translate('ssoSaml.signatureAlgorithm')}:</dt>
                      <dd>{translate('ssoSaml.signatureAlgorithmType')}</dd>
                    </div>
                    <div className={styles.item}>
                      <dt>{translate('ssoSaml.expiresOn')}:</dt>
                      <dd>{moment(item.expiration_date).utc().format('MMMM DD, YYYY')}</dd>
                    </div>
                  </dl>
                </div>
              )
            })}

            <div className={styles.certificateActions}>
              <div className={styles.browseFileContainer}>
                <button>
                  <FontAwesomeIcon icon={faArrowFromBottom as IconProp}/>
                  <label className={styles.browseFileLabelNoPadding}>
                    <span>{translate('common.form.buttons.uploadNewCertificate')}</span>
                    <input
                      ref={certificateReaderRef}
                      className={styles.browseFileInput}
                      type='file'
                      onChange={(e) => uploadCertificate(e)}
                    />
                  </label>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  const copyToClipboard = (textToCopy): void => {
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1000)
    });
  }

  const connectDigicertView = () => {
    return (
      <div className={styles.connectIdp}>
        <p>{translate('ssoSaml.downloadDigiCertMetaDataDescription')}</p>
        <Button buttonType={ButtonType.SECONDARY} icon={<Icon icon={faArrowToBottom}/>} onClick={downLoadMetaData}>
          {translate('common.form.buttons.downloadMetaData')}
        </Button>
        <Divider value={translate('singleSignOn.useThisUrl') as string}/>
        <TextArea
          size={FormFieldSize.XXL}
          value={federation.service_provider_config ? federation.service_provider_config.acs_url:''}
          label={translate('singleSignOn.ssoUrl')}
          readOnly
        />
        {!copied ? <div className={styles.copyToClipBoardContainer} onClick={() => copyToClipboard(federation.service_provider_config?.acs_url)}>
            <FontAwesomeIcon size='lg' icon={faCopy as IconProp} />
            <Translate id='common.form.buttons.copy' />
          </div> :
          <div className={cn(styles.copyToClipBoardContainer,styles.copiedMsg)}>
            <FontAwesomeIcon size='lg' icon={faCheck as IconProp} />
            <Translate id='openIdConnectIntegration.copied' />
          </div>
        }
      </div>
    )
  }

  const enableDisableSsoView = () => {
    const standardSignIn = account?.sign_in_methods.find(item => item.signInMethod === 'standard')
    return (
      <div className={styles.sso}>
        <div className={styles.content}>
          <Tooltip
            autoAdjustOverflow
            title={translate('ssoSaml.disablingSsoMessage')}
            placement='right'
            getPopupContainer={(triggerNode) => triggerNode}
            open={isSsoEnabled}
          >
            <Switch
              disabled={standardSignIn?.status === DISABLED}
              checkedChildren={translate('common.form.buttons.on')}
              unCheckedChildren={translate('common.form.buttons.off')}
              onChange={handleSwitchChange}
              checked={isSsoEnabled}
            />
          </Tooltip>
        </div>
      </div>
    )
  }

  const formatNotificationMessage = () => {
    if(isSsoEnabled) {
      return translate('common.statuses.enabled') + ' ' + translate('ssoSaml.ssoWithSamlFor') + ' ' + account?.name
    }
    else {
      return translate('common.statuses.disabled') + ' ' + translate('ssoSaml.ssoWithSamlFor') + ' ' + account?.name
    }
  }

  const saveConfiguration = () => {
    // default id is in params else its being modified via platform settings with permissions
    const accountId = id ? id : primaryAccountId
    const payload = {
      auth_type: 'SAML 2.0',
      account_id: accountId,
      identity_provider_config: {
        metadata: {
          metadata: idpMetaData
        },
        certificates: idpCertificates
      },
      status: isSsoEnabled ? ENABLED : DISABLED
    }

    createIdpMetadata(payload, federation.id, formatNotificationMessage()).then(() => {
      if(onChange) {
        onChange(isSsoEnabled)
        if(onCancel) {
          onCancel()
        }
      }
    })
  }

  const createOrGetAccountSignIn = (payload: any, federationId: string): Promise<any> => {
    if (tenant && (tenant.auth_config == null || tenant.auth_config.find(item => item.type === 'SAML 2.0') == null)) {
      return createAccountSignIn(payload)
    } else {
      return getAccountSignIn(federationId)
    }
  }

  const downLoadMetaData = () => {
    if(!federation.isLoading && federation.service_provider_config !== undefined){
      const fileName = 'service_provider_metadata_' + federation.name.substring(federation.name.indexOf('_'), federation.name.length) + '.xml'
      fileDownload(federation.service_provider_config.metadata, fileName)
    }
  }

  const setData = (res) => {
    if(res.identity_provider_config && res.identity_provider_config.certificates.length > 0) {
      setIdpCertificates(res.identity_provider_config.certificates)
      setIdpMetaData(res.identity_provider_config.metadata.metadata)
      setIdpUrl(res.identity_provider_config.metadata_url)
      for (let i = 0; i < res.identity_provider_config.certificates.length; i ++) {
        const thumbPrint = res.identity_provider_config.certificates[i].thumb_print ? res.identity_provider_config.certificates[i].thumb_print : ''
        setThumbprints([...thumbprints, thumbPrint])
      }
    }
  }

  useEffect(() => {
    if(tenant){
      const payload = {
        auth_type: 'SAML 2.0',
        account_id: tenant.id
      }
      const federationProvider = tenant.auth_config ? tenant.auth_config.find(item => item.type === 'SAML 2.0') : undefined
      const federationId = federationProvider ? federationProvider.id : ''

      createOrGetAccountSignIn(payload, federationId).then((res) => {
        setAccountSigninResponse(res)
        setData(res)
      })
    }
  }, [tenant])

  useEffect(() => {
    if (accountSigninResponse !== undefined) {
      setData(accountSigninResponse)
    }
  },[onCancel])

  useEffect(() => {
    // file reader value must be reset to recognize file change when selecting and deleting same file
    if(fileReaderRef.current) {
      fileReaderRef.current.value = ''
    }
    if(certificateReaderRef.current) {
      certificateReaderRef.current.value = ''
    }
  }, [idpCertificates]);

  return (
    <div className={styles.ssoConfig}>
      <Accordion
        showArrow
        type={AccordionTypes.BORDERED}
        oneByOne={false}
        accordionData={[
          {
            headline: translate('ssoSaml.connectIdp') as string,
            key: '1',
            managerIcon: <StepOneIcon/>,
            view: connectIdpView(),
            anchorId: 'step-1',
            notCollapsible: false,
            disablePanel: false
          },
          {
            headline: translate('ssoSaml.connectDigiCert') as string,
            key: '2',
            managerIcon: <StepTwoIcon/>,
            view: connectDigicertView(),
            anchorId: 'step-2',
            notCollapsible: false,
            disablePanel: false
          },
          {
            headline: translate('ssoSaml.enableDisableSsoWithSaml') as string,
            key: '3',
            managerIcon: <StepThreeIcon/>,
            view: enableDisableSsoView(),
            anchorId: 'step-3',
            notCollapsible: false,
            disablePanel: false
          }
        ]}
      />
      <div className={styles.actions}>
        <Button buttonType={ButtonType.SECONDARY} onClick={cancelSso}>{translate('common.form.buttons.cancelBtn')}</Button>
        <Button buttonType={ButtonType.PRIMARY} onClick={saveConfiguration} disabled={idpCertificates.length === 0}>{translate('common.form.buttons.saveConfiguration')}</Button>
        <Link
          to={{ pathname: 'https://docs.digicert.com/en/digicert-one/account-manager/single-sign-on-with-saml/configure-single-sign-on-with-saml.html'}}
          target='_blank'>
          {translate('common.form.buttons.needHelp') as string}
        </Link>
      </div>
    </div>
  )
}

export default connect(
  (state: IAppState): SsoConfigStateProps => ({
    federation: state.federation
  }),
  {createAccountSignIn, createIdpMetadata, getAccountSignIn}
)(SsoConfig)
